import { useState, useEffect } from 'react';
import _ from 'lodash';
import isURL from 'validator/lib/isURL';
import {
  getAccess,
  getAuthKeys,
  getAuthUrl,
  getState
} from 'common/services/jira-auth.service';
import { JIRA_AUTH_STATE, TRACKING } from 'common/constants/constants';
import * as TrackingService from 'common/services/tracking.service';
import {
  checkConnectivityStatus,
  getVersionErrorMessage,
  isWaitingForUserInputs,
  openAuthPage,
  step1ErrorMeesages
} from '../jiraSteps.common';
import { useSelector } from 'react-redux';
import { getJiraSensorAuthorizationSelector } from 'state/selectors/sensors-authorizations';
import { updateSensorAuthorization } from 'common/services/sensors.service';
import useToast from 'components/hooks/useToast';
import { myAccountSelector } from 'state/selectors/account';
import useDispatchify from 'common/hooks/utils/useDispatchify';
import { getSensorAuthorizations as getSensorAuthorizationsCaller } from 'state/callers';

const useJiraSetupStates = () => {
  const [baseJiraUrl, setBaseJiraUrl] = useState('');
  const [step1Enabled, setStep1Enabled] = useState(false);
  const [step2Enabled, setStep2Enabled] = useState(false);
  const [step3Enabled, setStep3Enabled] = useState(false);
  const [step1Error, setStep1Error] = useState(null);
  const [jiraState, setJiraState] = useState(null);
  const [finish, setFinish] = useState(false);
  const [authKeys, setAuthKeys] = useState({});
  const [authUrl, setAuthUrl] = useState(null);
  const jiraSensor = useSelector(getJiraSensorAuthorizationSelector);
  const toast = useToast();
  const account = useSelector(myAccountSelector);
  const getSensorAuthorizations = useDispatchify(getSensorAuthorizationsCaller);

  const oAuthStepsStateMachine = async jiraState => {
    if (!isWaitingForUserInputs(jiraState)) {
      return;
    }
    try {
      const authKeys = await getAuthKeys(account.id, baseJiraUrl);
      const versionError = getVersionErrorMessage(_.get(authKeys, 'version'));
      const step1Enabled = !!versionError;
      setStep1Enabled(step1Enabled);
      setStep1Error(versionError);
      setStep2Enabled(!step1Enabled);
      setAuthKeys(authKeys);
    } catch (e) {
      setStep1Enabled(true);
    }
  };

  const fetchAuthURLAndSetInState = async () => {
    try {
      const authUrl = await getAuthUrl(baseJiraUrl);
      setAuthUrl(authUrl);
      setStep3Enabled(true);
      openAuthPage(authUrl);
      setFinish(true);
    } catch (e) {
      toast({
        message:
          'We had a problem connecting to Jira. Please make sure your Jira URL is correct and that you completed all the required steps before pressing Continue',
        variant: 'error'
      });
    }
  };

  const finishOnboardingProcess = async () => {
    try {
      await getAccess();
      getSensorAuthorizations();
      setJiraState(JIRA_AUTH_STATE.AUTHORIZED);
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.JIRA,
        click_type: TRACKING.CLICK.SETUP_PM_CONNECT_COMPLETED
      });
    } catch {
      if (jiraSensor) {
        await updateSensorAuthorization(jiraSensor.id, {
          state: JIRA_AUTH_STATE.FAILING
        });
      }
      setJiraState(JIRA_AUTH_STATE.FAILING);
      toast({ message: 'Access Denied', variant: 'error' });
    }
  };

  const postStep1 = async () => {
    const response = await checkConnectivityStatus(baseJiraUrl);

    if (!response) {
      setStep1Error(step1ErrorMeesages.connectivity);
      return;
    }

    try {
      const authKeys = await getAuthKeys(account.id, baseJiraUrl);
      const versionError = getVersionErrorMessage(_.get(authKeys, 'version'));
      if (versionError) {
        throw new Error(versionError);
      }
      setAuthKeys(authKeys);
      setStep1Enabled(false);
      setStep1Error(null);
      setStep2Enabled(true);
    } catch (e) {
      setStep1Error(step1ErrorMeesages.not_jira);
    }
  };

  const onUrlChange = url => {
    setBaseJiraUrl(url);
    setStep1Enabled(isURL(url));
  };

  useEffect(() => {
    const loadJiraState = async () => {
      try {
        const { state: jiraState, base_url } = await getState();
        setJiraState(jiraState);
        setBaseJiraUrl(base_url ?? '');
      } catch (e) {}

      TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
        page: TRACKING.PAGES.SETTINGS,
        page_tab: TRACKING.PAGE_TABS.JIRA
      });
    };
    loadJiraState();
  }, []);

  useEffect(() => {
    if (jiraState && baseJiraUrl) {
      oAuthStepsStateMachine(jiraState);
    }
  }, [jiraState, baseJiraUrl]);

  return {
    step1Enabled,
    step1Error,
    step2Enabled,
    step3Enabled,
    state: jiraState,
    onStep1Finish: postStep1,
    onStep2Finish: fetchAuthURLAndSetInState,
    onStep3Finish: finishOnboardingProcess,
    authKeys,
    authUrl,
    baseJiraUrl,
    onUrlChange,
    finish,
    setFinish
  };
};

export default useJiraSetupStates;
